// Package imports:
import React, { useState, useEffect } from 'react';
import cx from 'classnames';
// Component imports:
import CompanyWatchButton from '../CompanyWatchButton/CompanyWatchButton';
import Icon from '../../Header/CustomIcon/CustomIcon';
// Service imports:
import useComponentVisible from '../../../services/hooks';
// Type imports:
import { ICompanyViewModel } from '../../../types/CompanyTypes';

const CompanyNavBar: React.FC<ICompanyViewModel> = ({
    id,
    isAuthenticated,
    isSubscriber
}) => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible();

    // State to track current URL path (pathname + hash)
    const [currentPath, setCurrentPath] = useState('');

    /**
     * Updates the current path state when the component mounts or URL changes
     * This combines the pathname and hash to get the full URL path
     */
    useEffect(() => {
        // Function to update the current path from window.location
        const updatePath = () => {
            setCurrentPath(`${window.location.pathname}${window.location.hash}`);
        };

        // Set initial path on component mount
        updatePath();

        // Listen for URL changes (when user navigates using browser back/forward buttons)
        window.addEventListener('popstate', updatePath);

        // Listen for hash changes (when hash part of URL changes)
        window.addEventListener('hashchange', updatePath);

        // Cleanup: remove event listeners when component unmounts
        return () => {
            window.removeEventListener('popstate', updatePath);
            window.removeEventListener('hashchange', updatePath);
        };
    }, []);

    /**
     * Checks if the current URL matches a given path pattern
     * @param path - The path to match against (can include hash)
     * @returns boolean - true if the current URL starts with the given path
     */
    const isActive = (segment: string): boolean => {
        const base = `/Fyrirtaeki/${segment}`;
        return currentPath.startsWith(`${base}/`) || currentPath.startsWith(`${base}-`) || currentPath.startsWith(`${base}?`);
    };

    return (
        <div className="KCL_sub-navbar">
            {/* Mobile menu version */}
            <ul className="mobileMenu">
                <li>
                    <span
                        className='list-category'
                        id="mobile"
                        onClick={() => setIsComponentVisible(!isComponentVisible)}
                    >
                        Yfirlit fyrirtækis <Icon type='dropdown-arrow' />
                    </span>
                    <ul ref={ref} className={cx("mobile dropdown", { "focused": isComponentVisible })}>
                        {/* Company overview link - active when on /Fyrirtaeki/Yfirlit/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Yfirlit/${id}`}
                                className={cx({ 'active': isActive('Yfirlit') })}
                            >
                                Yfirlit fyrirtækis
                            </a>
                        </li>

                        {/* Reports section */}
                        <li className='list'>
                            <span>Skýrslur</span>
                            <ul className='list-items'>
                                {/* Annual reports - active when on /Fyrirtaeki/Arsreikningar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Arsreikningar/${id}`}
                                        className={cx({ 'active': isActive('Arsreikningar') })}
                                    >
                                        Ársreikningaskrá
                                    </a>
                                </li>
                                {/* Company registry - active when on /Fyrirtaeki/Hlutafelagaskra/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Hlutafelagaskra/${id}`}
                                        className={cx({ 'active': isActive('Hlutafelagaskra') })}
                                    >
                                        Hlutafélagaskrá
                                    </a>
                                </li>
                                {/* Business registry - active when on /Fyrirtaeki/Fyrirtaekjaskra/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Fyrirtaekjaskra/${id}`}
                                        className={cx({ 'active': isActive('Fyrirtaekjaskra') })}
                                    >
                                        Fyrirtækjaskrá
                                    </a>
                                </li>
                                {/* Entered annual reports - active when on /Fyrirtaeki/Innslegnir-Arsreikningar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`}
                                        className={cx({ 'active': isActive('Innslegnir-Arsreikningar') })}
                                    >
                                        Innslegnir ársreikningar
                                    </a>
                                </li>
                                {/* Comparison - active when on /Fyrirtaeki/Samanburdur/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Samanburdur/${id}`}
                                        className={cx({ 'active': isActive('Samanburdur') })}
                                    >
                                        Samanburður
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Assets section */}
                        <li className='list'>
                            <span>Eignir</span>
                            <ul className='list-items'>
                                {/* Real estate - active when on /Fyrirtaeki/Fasteignir/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Fasteignir/${id}`}
                                        className={cx({ 'active': isActive('Fasteignir') })}
                                    >
                                        Fasteignir
                                    </a>
                                </li>
                                {/* Vehicles - active when on /Fyrirtaeki/Okutaeki/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Okutaeki/${id}`}
                                        className={cx({ 'active': isActive('Okutaeki') })}
                                    >
                                        Ökutæki
                                    </a>
                                </li>
                                {/* Machinery - active when on /Fyrirtaeki/Vinnuvelar/{id} */}
                                <li>
                                    <a
                                        href={`/Fyrirtaeki/Vinnuvelar/${id}`}
                                        className={cx({ 'active': isActive('Vinnuvelar') })}
                                    >
                                        Vinnuvélar
                                    </a>
                                </li>
                            </ul>
                        </li>

                        {/* Legal publications - active when on /Fyrirtaeki/Logbirtingar/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Logbirtingar/${id}`}
                                className={cx({ 'active': isActive('Logbirtingar') })}
                            >
                                Lögbirtingar
                            </a>
                        </li>

                        {/* Valuation - active when on /Fyrirtaeki/Verdmat/{id} */}
                        <li>
                            <a
                                href={`/Fyrirtaeki/Verdmat/${id}`}
                                className={cx({ 'active': isActive('Verdmat') })}
                            >
                                Verðmat
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>

            {/* Desktop menu version */}
            <ul className='itemList'>
                {/* Company overview link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive('Yfirlit') })}
                        href={`/Fyrirtaeki/Yfirlit/${id}`}
                    >
                        Yfirlit fyrirtækis
                    </a>
                </li>

                {/* Reports dropdown - parent is active if any child report page is active */}
                <li>
                    <span
                        className={cx('list-category', {
                            'active': isActive('Arsreikningar') ||
                                isActive('Hlutafelagaskra') ||
                                isActive('Fyrirtaekjaskra') ||
                                isActive('Innslegnir-Arsreikningar') ||
                                isActive('Samanburdur')
                        })}
                    >
                        Skýrslur <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href={`/Fyrirtaeki/Arsreikningar/${id}`}
                                className={cx({ 'active': isActive('Arsreikningar') })}
                            >
                                Ársreikningaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Hlutafelagaskra/${id}`}
                                className={cx({ 'active': isActive('Hlutafelagaskra') })}
                            >
                                Hlutafélagaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Fyrirtaekjaskra/${id}`}
                                className={cx({ 'active': isActive('Fyrirtaekjaskra') })}
                            >
                                Fyrirtækjaskrá
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Innslegnir-Arsreikningar/${id}`}
                                className={cx({ 'active': isActive('Innslegnir-Arsreikningar') })}
                            >
                                Innslegnir ársreikningar
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Samanburdur/${id}`}
                                className={cx({ 'active': isActive('Samanburdur') })}
                            >
                                Samanburður
                            </a>
                        </li>
                    </ul>
                </li>

                {/* Assets dropdown - parent is active if any child asset page is active */}
                <li>
                    <span
                        className={cx('list-category', {
                            'active': isActive('Fasteignir') ||
                                isActive('Okutaeki') ||
                                isActive('Vinnuvelar')
                        })}
                    >
                        Eignir <span className='icon'><Icon type='dropdown-arrow' /></span>
                    </span>
                    <ul className="dropdown">
                        <li>
                            <a
                                href={`/Fyrirtaeki/Fasteignir/${id}`}
                                className={cx({ 'active': isActive('Fasteignir') })}
                            >
                                Fasteignir
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Okutaeki/${id}`}
                                className={cx({ 'active': isActive('Okutaeki') })}
                            >
                                Ökutæki
                            </a>
                        </li>
                        <li>
                            <a
                                href={`/Fyrirtaeki/Vinnuvelar/${id}`}
                                className={cx({ 'active': isActive('Vinnuvelar') })}
                            >
                                Vinnuvélar
                            </a>
                        </li>
                    </ul>
                </li>

                {/* Legal publications link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive('Logbirtingar') })}
                        href={`/Fyrirtaeki/Logbirtingar/${id}`}
                    >
                        Lögbirtingar
                    </a>
                </li>

                {/* Valuation link */}
                <li>
                    <a
                        className={cx('list-category', { 'active': isActive('Verdmat') })}
                        href={`/Fyrirtaeki/Verdmat/${id}`}
                    >
                        Verðmat
                    </a>
                </li>
            </ul>
            <CompanyWatchButton isAuthenticated={isAuthenticated} isSubscriber={isSubscriber} id={id ?? ''} />
        </div>
    );
};

export default CompanyNavBar;